import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falFingerprint } from 'shared/@generated/icons/falFingerprint';
import { falMessageHeart } from 'shared/@generated/icons/falMessageHeart';
import { falShieldKeyhole } from 'shared/@generated/icons/falShieldKeyhole';
import { useRoute } from 'shared/library/routes/route';
import { withNativeGradientFallback } from 'shared/library/native-gradient';
import { Platform, View } from 'react-native';
import { Listing, Paginator, Props, Breed, Animal, CountryCode } from 'shared/@types';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'shared/components/link';
import { apiRoute } from 'shared/library/api-route';
import { PageBuilder } from 'shared/library/page-builder';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { Image } from 'shared/components/image';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { ListingCard } from 'shared/components/listing-card';
import { BreedCard } from 'shared/components/breed-card';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { HeroListingsCluster } from 'shared/components/hero-listings-cluster';
import { Browse } from 'shared/components/illustrations/browse';
import { Reservation } from 'shared/components/illustrations/reservation';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import CommentHeart from 'shared/assets/doodles/comment-heart.png';
import { ReactNode } from 'react';
import { clsx } from 'clsx';
import { translateArray } from 'shared/library/i18n/get-translation-list';
import { getCountryName } from 'shared/library/i18n/country-name';
import { loadBuyingADogListings } from 'shared/library/buying-a-dog-page-data';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';

interface BuyingADogScreenProps extends Props {
  listings: Paginator<Listing>;
  breeds: Paginator<Breed>;
  listingCountry: CountryCode;
}

const BuyingADogScreen = (props: BuyingADogScreenProps) => (
  <AdAnimalProvider animal={'dog'}>
    <BuyingADogContent {...props} />
  </AdAnimalProvider>
);

const BuyingADogContent = ({ listings, breeds, listingCountry }: BuyingADogScreenProps) => {
  const { t } = useTranslation('screens');
  const listingCountryName = getCountryName(t, listingCountry) ?? listingCountry;
  const route = useRoute();
  const isNative = Platform.OS !== 'web';

  const faqItems = translateArray<{ question: string; answer: string }>(
    t,
    'buying_a_dog.faq.items',
    {
      country: listingCountryName,
    },
  );

  const supportItems = [
    {
      title: t('buying_a_dog.hero.support_items.verified_profiles.title'),
      text: t('buying_a_dog.hero.support_items.verified_profiles.text'),
      icon: falFingerprint,
    },
    {
      title: t('buying_a_dog.hero.support_items.petpay.title'),
      text: t('buying_a_dog.hero.support_items.petpay.text'),
      icon: falShieldKeyhole,
    },
    {
      title: t('buying_a_dog.hero.support_items.personal_help.title'),
      text: t('buying_a_dog.hero.support_items.personal_help.text'),
      icon: falMessageHeart,
    },
  ];

  const processItems = translateArray<{
    title?: string;
    text?: string;
  }>(t, 'buying_a_dog.process.items');

  const journeyImages: ReactNode[] = [
    <Browse key={'browse'} />,
    <Reservation key={'reservation'} />,
    <Payment key={'payment'} />,
    <Pickup key={'pickup'} />,
  ];
  const journeyItems: Array<{ title: string; text: string; image: ReactNode }> = processItems
    .slice(0, journeyImages.length)
    .flatMap((item, index) => {
      if (!item?.title || !item?.text) {
        return [];
      }

      return [
        {
          title: item.title,
          text: item.text,
          image: journeyImages[index]!,
        },
      ];
    });

  const heroListings = listings.data.filter(
    (listing) => listing.media_assets?.[0]?.url || listing.avatar?.url,
  );

  const listingsCount = listings.total ?? listings.data.length;
  const localListingsQuery = { country: listingCountry };
  const heroListingSource = heroListings.length > 0 ? heroListings : listings.data;

  const previewHeroListings: Listing[] =
    heroListingSource.length > 0
      ? Array.from(
          { length: 7 },
          (_, index) => heroListingSource[index % heroListingSource.length]!,
        )
      : [];

  const stats = [
    {
      value: '>100K',
      label: t('buying_a_dog.stats.sold_pets'),
    },
    {
      value: '>4K',
      label: t('buying_a_dog.stats.breeders'),
    },
    {
      value: '2019',
      label: t('buying_a_dog.stats.founded'),
    },
  ];

  return (
    <View>
      <View
        className={withNativeGradientFallback(
          clsx(
            'from-brand-cream-light via-brand-cream-light to-brand-cream bg-gradient-to-b',
            !isNative && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
          ),
        )}
      >
        <View className={'px-3 py-6 md:px-6 md:py-10'}>
          <View className={'mx-auto w-full max-w-screen-xl'}>
            <View
              className={
                'flex flex-col-reverse gap-5 md:min-h-[38rem] md:flex-row md:items-center md:justify-between md:gap-8 xl:gap-10'
              }
            >
              <View className={'max-w-3xl flex-1 md:min-w-0 md:py-6'}>
                <H1
                  title={t('buying_a_dog.hero.title', { country: listingCountryName })}
                  hero={true}
                  className={'max-w-xl'}
                  marginBottom={false}
                />

                <Text
                  className={
                    'text-brand-brown-dark/80 mt-5 max-w-xl text-lg leading-relaxed md:text-xl'
                  }
                >
                  {t('buying_a_dog.hero.text', { country: listingCountryName })}
                </Text>

                <View className={'mt-8 gap-3 md:flex-row'}>
                  <Button
                    text={t('buying_a_dog.hero.view_listings')}
                    icon={falAngleRight}
                    color={'brown'}
                    href={route('listings.dogs', undefined, { query: localListingsQuery })}
                    className={'min-w-[13rem] transition-colors duration-200 hover:opacity-90'}
                  />
                  <Button
                    text={t('buying_a_dog.hero.view_breeds')}
                    icon={falAngleRight}
                    color={'white'}
                    href={route('breeds.dogs')}
                    className={'hover:border-brand-brown-dark/30 transition-colors duration-200'}
                  />
                </View>

                <View className={'mt-6 self-center md:mt-10 md:self-start'}>
                  <View className={'relative pl-10 md:pl-14'}>
                    <View className={'absolute left-0 top-1/2 -translate-y-1/2'}>
                      <Image
                        src={CommentHeart}
                        alt={'Doodle'}
                        containerClassName={'size-9 md:size-12'}
                      />
                    </View>

                    <ReviewsSummaryCard hero={true} />
                  </View>
                </View>
              </View>

              <View
                className={
                  isNative
                    ? 'w-full'
                    : 'mt-8 w-full md:mx-0 md:mt-10 md:w-[25.5rem] md:flex-none lg:w-[29rem] xl:w-[33rem]'
                }
              >
                <HeroListingsCluster listings={previewHeroListings} />
              </View>
            </View>

            <View className={isNative ? 'mt-10 gap-6' : 'mt-10 grid gap-6 sm:grid-cols-3 sm:gap-8'}>
              {supportItems.map((item) => (
                <View key={item.title} className={'flex-row items-start gap-3 sm:pr-3'}>
                  <View
                    className={
                      'bg-brand-cream-dark flex size-10 shrink-0 items-center justify-center rounded-md'
                    }
                  >
                    <Icon icon={item.icon} className={'text-brand-brown-dark size-4'} />
                  </View>

                  <View className={'flex-1 gap-2'}>
                    <Text className={'text-brand-brown-dark text-lg font-semibold leading-6'}>
                      {item.title}
                    </Text>

                    <Text className={'text-brand-brown-dark/72 max-w-sm text-base leading-7'}>
                      {item.text}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'top'} />}

          <View className={'mx-auto max-w-screen-lg'}>
            <H2
              title={t('buying_a_dog.listings.title', { country: listingCountryName })}
              subtitle={t('buying_a_dog.listings.text', {
                country: listingCountryName,
                count: listingsCount,
              })}
              center={true}
              marginTop={false}
            />
          </View>

          <View
            className={
              isNative
                ? 'mt-6 gap-6'
                : 'flex-cols-1 md:flex-cols-2 lg:flex-cols-4 mt-6 flex-row flex-wrap gap-6'
            }
          >
            {listings.data.slice(0, 8).flatMap((listing, index) => {
              const items = [
                <View key={listing.id}>
                  <ListingCard listing={listing} />
                </View>,
              ];

              if (!isNative && index === 1) {
                items.push(
                  <View key={`listing-mobile-ad-${listing.id}`} className={'w-full md:hidden'}>
                    <AdBanner loadImmediately={true} placement={'content'} />
                  </View>,
                );
              }

              return items;
            })}
          </View>

          <View className={'mt-8 items-start md:mt-10 md:items-center'}>
            <Button
              text={t('buying_a_dog.listings.cta', { country: listingCountryName })}
              icon={falAngleRight}
              color={'brown'}
              href={route('listings.dogs', undefined, { query: localListingsQuery })}
            />
          </View>
        </View>
      </View>

      <View
        className={clsx(
          'bg-brand-cream-light p-3 md:p-6 md:py-20',
          !isNative && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
        )}
      >
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            id={'how-it-works'}
            title={t('buying_a_dog.process.title')}
            subtitle={t('buying_a_dog.process.text')}
            center={true}
            marginTop={false}
          />

          <View className={'mx-auto mt-3 max-w-screen-md gap-6'}>
            {journeyItems.map((item, index) => (
              <View
                key={item.title}
                className={clsx(
                  'gap-3 md:flex-row md:items-center md:gap-6',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse',
                )}
              >
                <View className={'w-full md:w-2/5'}>{item.image}</View>

                <View className={'w-full md:flex-1'}>
                  <Text
                    accessibilityRole={'header'}
                    aria-level={3}
                    className={'text-brand-brown-dark font-bold md:text-lg'}
                  >
                    {index + 1}. {item.title}
                  </Text>
                  <Text className={'text-brand-brown-dark/75 md:text-lg'}>{item.text}</Text>
                  {index === 1 && (
                    <Text className={'text-brand-brown-dark/75 mt-2 md:text-lg'}>
                      <Trans
                        t={t}
                        i18nKey={'buying_a_dog.guides.breeder_questions'}
                        components={{
                          guide: (
                            <Link
                              href={route('blog.show', {
                                post: {
                                  en: '14-important-questions-to-ask-the-breeder-when-buying-a-dog',
                                  sv: '14-viktiga-fragor-att-stalla-till-uppfodaren-nar-du-koper-hund',
                                  da: '14-vigtige-sporgsmal-til-opdroetter-ved-hundekoeb',
                                  no: '14-viktige-sporsmal-du-bor-stille-oppdretteren-nar-du-kjoper-hund',
                                },
                              })}
                              asInline
                              containerClassName={'underline'}
                              textClassName={'underline'}
                            />
                          ),
                        }}
                      />
                    </Text>
                  )}
                </View>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_dog.why.title')}
            subtitle={t('buying_a_dog.why.text', { country: listingCountryName })}
            center={true}
            marginTop={false}
          />

          <View
            className={
              isNative
                ? 'mx-auto mt-8 max-w-5xl gap-8'
                : 'mx-auto mt-8 grid max-w-5xl gap-8 md:grid-cols-3 md:gap-10'
            }
          >
            {stats.map((stat) => (
              <View key={stat.label} className={'items-center'}>
                <Text
                  className={'text-brand-brown-dark text-center text-2xl font-black md:text-3xl'}
                >
                  {stat.value}
                </Text>
                <Text className={'text-brand-brown-dark/70 mt-2 max-w-[18rem] text-center'}>
                  {stat.label}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_dog.breeds.title')}
            subtitle={t('buying_a_dog.breeds.text')}
            center={true}
            marginTop={false}
          />

          <Text className={'text-brand-brown-dark/75 mt-3 text-center md:text-lg'}>
            <Trans
              t={t}
              i18nKey={'buying_a_dog.guides.choosing_breed'}
              components={{
                guide: (
                  <Link
                    href={route('blog.show', {
                      post: {
                        en: 'how-to-choose-the-right-dog-breed',
                        sv: 'sa-valjer-du-ratt-hundras',
                        da: 'sa-valger-du-ret-hunderace',
                        no: 'slik-velger-du-riktig-hunderase',
                      },
                    })}
                    asInline
                    containerClassName={'underline'}
                    textClassName={'underline'}
                  />
                ),
              }}
            />
          </Text>

          <View
            className={
              isNative
                ? 'mt-6 flex-row flex-wrap gap-3'
                : 'flex-cols-2 md:flex-cols-5 mt-6 flex-row gap-3 md:gap-6'
            }
          >
            {breeds.data.slice(0, 10).map((breed) => (
              <View key={breed.id} style={isNative ? { width: '48%' } : undefined}>
                <BreedCard breed={breed} showActiveCount={true} />
              </View>
            ))}
          </View>

          <View className={'mt-8 items-start md:mt-10 md:items-center'}>
            <Button
              text={t('buying_a_dog.breeds.cta')}
              icon={falAngleRight}
              color={'brown'}
              href={route('breeds.dogs')}
            />
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'content'} />}

          <View>
            <H2
              title={t('buying_a_dog.faq.title')}
              subtitle={t('buying_a_dog.faq.text')}
              center={true}
              marginTop={false}
            />

            <View className={isNative ? 'mt-6 gap-6' : 'mt-6 grid gap-6 md:grid-cols-2'}>
              {faqItems.map((item) => (
                <View key={item.question} className={'pb-2'}>
                  <Text
                    accessibilityRole={'header'}
                    aria-level={3}
                    className={'text-brand-brown-dark font-bold md:text-lg'}
                  >
                    {item.question}
                  </Text>
                  <Text className={'text-brand-brown-dark/75 mt-2'}>{item.answer}</Text>
                </View>
              ))}
            </View>
          </View>

          <View className={'mt-12'}>
            <ReviewsSegment role={'buyer'} />
          </View>
        </View>
      </View>

      <View className={'bg-brand-cream p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          <H2
            title={t('buying_a_dog.closing.title')}
            subtitle={t('buying_a_dog.closing.text')}
            center={true}
            marginTop={false}
          />

          <View className={'mt-3 flex-col items-center gap-3 md:flex-row md:justify-center'}>
            <Button
              text={t('buying_a_dog.closing.primary_cta')}
              icon={falAngleRight}
              color={'brown'}
              href={route('listings.dogs', undefined, { query: localListingsQuery })}
            />
            <Button
              text={t('buying_a_dog.closing.secondary_cta')}
              icon={falAngleRight}
              color={'white'}
              href={route('breeds.dogs')}
            />
          </View>
        </View>
      </View>
    </View>
  );
};

BuyingADogScreen.pageBuilder = new PageBuilder<BuyingADogScreenProps>()
  .request(({ req, ctx }) => loadBuyingADogListings(req, ctx))
  .request(({ req }) =>
    req<{ breeds: Paginator<Breed> }>(apiRoute('breeds.recommended'), {
      animal: Animal.Dog,
      per_page: 10,
    }),
  );

export { BuyingADogScreen };
export type { BuyingADogScreenProps };
